import { type HeatmapConfig } from "./types";

// 默认的最小/最大面积占比，用于避免极端大/极端小格子
const DEFAULT_MIN_AREA_RATIO = 0.018;
const DEFAULT_MAX_AREA_RATIO = 0.25;

/**
 * 使用平方根变换对市值进行归一化，生成 treemap 使用的权重数组。
 *
 * 步骤：
 * 1. 对每个有效市值取 sqrt，弱化极端大市值的影响；
 * 2. 归一化得到初始占比；
 * 3. 应用 minAreaRatio / maxAreaRatio 进行裁剪，避免面积过小或过大；
 * 4. 再次归一化为和为 1 的权重数组。
 *
 * @param marketCaps 市值列表，可以包含 null / undefined / <= 0
 * @param config 从 HeatmapConfig 中透传的面积配置（可选）
 * @returns 权重数组，长度与 marketCaps 相同，所有元素之和为 1
 */
export function computeAreaWeightsUsingSqrt(
  marketCaps: Array<number | null | undefined>,
  config?: Pick<HeatmapConfig, "minAreaRatio" | "maxAreaRatio">,
): number[] {
  const minAreaRatio = config?.minAreaRatio ?? DEFAULT_MIN_AREA_RATIO;
  const maxAreaRatio = config?.maxAreaRatio ?? DEFAULT_MAX_AREA_RATIO;

  if (!marketCaps.length) {
    return [];
  }

  // 1. 先对所有市值做 sqrt 变换
  const transformed = marketCaps.map((value) => {
    if (value == null || value <= 0) {
      return 0;
    }
    return Math.sqrt(value);
  });

  let sum = transformed.reduce((acc, v) => acc + v, 0);

  // 所有值都无效时，退化为均分
  if (sum === 0) {
    const equalWeight = 1 / marketCaps.length;
    return marketCaps.map(() => equalWeight);
  }

  // 2. 初始归一化
  let normalized = transformed.map((v) => v / sum || 0);

  // 3. 应用最小/最大面积约束
  normalized = normalized.map((w) => {
    if (w === 0) {
      return minAreaRatio;
    }
    if (w < minAreaRatio) {
      return minAreaRatio;
    }
    if (w > maxAreaRatio) {
      return maxAreaRatio;
    }
    return w;
  });

  // 4. 二次归一化，使总和为 1
  sum = normalized.reduce((acc, v) => acc + v, 0);
  if (sum === 0) {
    const equalWeight = 1 / normalized.length;
    return normalized.map(() => equalWeight);
  }

  return normalized.map((w) => w / sum);
}
